import { Avatar, Box, Button, Typography } from '@mui/material';
import { groupBy, sortBy } from 'lodash';
import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EmployeeCalendarDate,
  EmployeeCalendarEvent,
  EmployeeCalendarPerson,
  getEmployeeCalendarPersonsFromEvents,
} from '~/components/EmployeeCalendar/data';
import { getProfilePictureUrl } from '~/utils/helper';

import {
  EmployeeCalendarCell,
  EmployeeCalendarCellGetters,
} from './EmployeeCalendarCell';
import { useSxStyles } from './useStyles';

export type EmployeeCalendarRowGetters = {
  onEmployeeClick?: (employeeId: number) => void;
  listEmployees?: EmployeeCalendarPerson[];
};

type PropsType = {
  events: EmployeeCalendarEvent[];
  datesInView: EmployeeCalendarDate[];
  minSizeRender: boolean;
  hideOutOfRange?: boolean;
  employeeNameHidden?: boolean;
} & EmployeeCalendarRowGetters &
  EmployeeCalendarCellGetters;

export const EmployeeCalendarRows: React.FC<PropsType> = (props: PropsType) => {
  const {
    events,
    datesInView,
    minSizeRender,
    getEventContent,
    getEventColors,
    onEmployeeClick,
    listEmployees,
    hideOutOfRange,
    employeeNameHidden,
  } = props;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const { eventsByEmployeeId, calendarEmployees } = useMemo(() => {
    const resolveEmployeesAndEvents = (
      events: EmployeeCalendarEvent[],
      listEmployees?: EmployeeCalendarPerson[],
    ) => {
      if (!!listEmployees && listEmployees.length > 0) {
        const filteredEvents = events.filter(({ employeeId }) =>
          listEmployees.some((employee) => employee.employeeId === employeeId),
        );

        return {
          events: filteredEvents,
          employees: listEmployees,
        };
      }

      // Resolve Employees from Events if listEmployees is undefined
      const resolvedEmployees =
        typeof listEmployees === 'undefined'
          ? getEmployeeCalendarPersonsFromEvents(events)
          : [];

      return {
        events,
        employees: resolvedEmployees,
      };
    };

    const resolved = resolveEmployeesAndEvents(events, listEmployees);

    const sortedEmployees = sortBy(
      resolved.employees,
      (employee) => employee.firstName,
    );

    const eventsByEmployeeId = groupBy(
      resolved.events,
      (event) => event.employeeId,
    );

    return {
      calendarEmployees: sortedEmployees,
      eventsByEmployeeId,
    };
  }, [events, listEmployees]);

  if (calendarEmployees.length === 0) {
    return <Box sx={sxStyles.noData}>{t('noDataAvailable')}</Box>;
  }

  return (
    <>
      {calendarEmployees.map(
        ({ employeeId, firstName, lastName, profilePic }) => {
          const employeeEvents = eventsByEmployeeId[employeeId] ?? [];

          const fullName = `${firstName} ${lastName}`;
          const initials = `${firstName?.charAt(0)}. ${lastName?.charAt(0)}.`;

          return (
            <Box key={employeeId} sx={sxStyles.rowWrapper}>
              {!employeeNameHidden && (
                <Typography
                  component={'div'}
                  sx={minSizeRender ? sxStyles.minNameCell : sxStyles.nameCell}
                  variant="body2"
                >
                  {minSizeRender &&
                    (!!onEmployeeClick ? (
                      <Button
                        sx={sxStyles.userName}
                        onClick={() => onEmployeeClick(employeeId)}
                      >
                        <Box component="span" sx={sxStyles.nameSpan}>
                          {initials}
                        </Box>
                      </Button>
                    ) : (
                      <Box component="span" sx={sxStyles.nameSpan}>
                        {initials}
                      </Box>
                    ))}

                  {!minSizeRender && (
                    <>
                      <Avatar
                        src={
                          !!profilePic
                            ? getProfilePictureUrl(profilePic)
                            : undefined
                        }
                        sx={sxStyles.avatar}
                      />
                      {!!onEmployeeClick ? (
                        <Button
                          sx={sxStyles.userName}
                          onClick={() => onEmployeeClick(employeeId)}
                        >
                          <Box component="span" sx={sxStyles.nameSpan}>
                            {fullName}
                          </Box>
                        </Button>
                      ) : (
                        <Box component="span" sx={sxStyles.nameSpan}>
                          {fullName}
                        </Box>
                      )}
                    </>
                  )}
                </Typography>
              )}
              {datesInView.map((calDate) => {
                return (
                  <EmployeeCalendarCell
                    key={calDate.id}
                    calendarDate={calDate}
                    events={employeeEvents}
                    getEventColors={getEventColors}
                    getEventContent={getEventContent}
                    hideOutOfRange={hideOutOfRange}
                  />
                );
              })}
            </Box>
          );
        },
      )}
    </>
  );
};
